import { useCallback, useEffect, useRef, useState } from 'react';
import { getErrorMessage } from '../../lib/errors';

const POLL_INTERVAL_MS = 2000;

// Shared polling primitive for every out-of-band admin/Diagnostics job
// (202 + job id, JobStore-backed). One implementation of the interval/
// progress/terminal-state/error handling so converted cards stop
// reimplementing it per panel (CLAUDE.md scale-boxed-operations rule: this
// class has been rediscovered four times already).
export function useJobPolling({ fetchStatus, onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const pollRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((jobId) => {
    stop();
    setPolling(true);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await fetchStatus(jobId);
        if (data.status === 'completed') {
          stop();
          onCompleted?.(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed?.(data.error || 'Run failed.');
        } else if (data.status === 'running') {
          onProgress?.(data.progress || null);
        }
      } catch (err) {
        stop();
        onFailed?.(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, fetchStatus, onCompleted, onFailed, onProgress]);

  return { polling, start, stop };
}

// Picks up an already-running job on mount so a page reload, a second tab,
// or navigating back to the panel resumes polling instead of losing track
// of an in-flight run (and re-showing enabled buttons over a job that's
// actually still going).
export function useJobResume({ fetchLatest, poll }) {
  useEffect(() => {
    (async () => {
      try {
        const { data } = await fetchLatest();
        if (data.job && data.job.status === 'running') {
          poll.start(data.job._id || data.job.id);
        }
      } catch {
        // Best-effort resume only -- a failed lookup here shouldn't block the panel.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export function JobProgressLine({ progress, testId, children }) {
  if (!progress) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid={testId}>
      {children ? children(progress) : JSON.stringify(progress)}
    </p>
  );
}

export function JobErrorBlock({ error, testId }) {
  if (!error) return null;
  return (
    <div
      className="p-3 rounded text-xs border bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-900"
      data-testid={testId}
    >
      <p className="text-red-600 dark:text-red-400">{error}</p>
    </div>
  );
}
